import { AbstractFilter } from "../types/AbstractFilter"
import { CatalogOperation } from "../types/CatalogOperation"
import { CharacteristicFilterRequest, FilterRequest } from "../types/request"

/**
 * Filter element restricting the product selection result based on a certain characteristic value or range.
 *
 * Must not be used in conjunction with ProductGroupFilter, ProductsFilter or BoolFilter, otherwise service responds with status code 400 (Bad Request).
 */
export class CharacteristicFilter implements AbstractFilter {
    private filter: CharacteristicFilterRequest

    constructor() {
        this.filter = {
            id: null
        }
    }

    /**
     * Convenience function for operations.
     * @param {String} op one of: <, <=, >, >=, ==, !=
     * @param {String|Number} value
     * @throws an error, if the operation is not known.
     */
    operation(op: CatalogOperation, value: string | number): this {
        switch (op) {
            case "<":
                if (typeof value === "number") {
                    return this.less(value)
                }
                return this
            case "<=":
                if (typeof value === "number") {
                    return this.lessOrEqual(value)
                }
                return this
            case ">":
                if (typeof value === "number") {
                    return this.greater(value)
                }
                return this
            case ">=":
                if (typeof value === "number") {
                    return this.greaterOrEqual(value)
                }
                return this
            case "==":
                return this.equal(value)
            case "!=":
                return this.notEqual(value)
            default:
                throw new Error(`CharacteristicFilter: unknown operation ${op}`)
        }
    }

    /**
     * ID of the characteristic on which the filter should be applied.
     * @param {String} cid the characteristic id
     * @return this
     */
    id(cid: string): this {
        this.filter.id = cid
        return this
    }

    /**
     * The value for an "equal" filter.
     * Must not be used in conjunction with notEqual, greater, greaterOrEqual, less or lessOrEqual, otherwise service responds with status code 400 (Bad Request).
     * @param {String|Number} value
     */
    equal(value: string | number): this {
        this.filter.eq = value
        return this
    }

    /**
     * The value for an "not equal" filter.
     * Must not be used in conjunction with notEqual, greater, greaterOrEqual, less or lessOrEqual, otherwise service responds with status code 400 (Bad Request).
     * @param {String|Number} value
     */
    notEqual(value: string | number): this {
        this.filter.ne = value
        return this
    }

    /**
     * The value for a ">" filter.
     * @param {Number} value
     */
    greater(value: number): this {
        this.filter.gt = value
        return this
    }

    /**
     * The value for a ">=" filter.
     * @param {Number} value
     */
    greaterOrEqual(value: number): this {
        this.filter.gte = value
        return this
    }

    /**
     * The value for a "<" filter.
     * @param {Number} value
     */
    less(value: number): this {
        this.filter.lt = value
        return this
    }

    /**
     * The value for a "<=" filter.
     * @param {Number} value
     */
    lessOrEqual(value: number): this {
        this.filter.lte = value
        return this
    }

    build(): FilterRequest {
        return {
            characteristicFilter: this.filter
        }
    }
}
